import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Tag, X } from 'lucide-react';
import Select from 'react-select';
import { selectCartItems, selectCartTotalAmount, clearCart } from '../store/slices/cartSlice';
import { pushPageView, pushBeginCheckout, pushAddShippingInfo, pushPurchase } from '../utils/gtm';

const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE || 'http://localhost:8000';

function validatePhone(phone) {
  const v = phone.trim();
  if (v.length !== 11) return 'Phone number must be exactly 11 digits.';
  if (!v.startsWith('01')) return 'Phone number must start with 01.';
  if (!/^\d+$/.test(v)) return 'Phone number must contain digits only.';
  return null;
}

const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderRadius: '0.75rem',
    borderColor: hasError ? '#f87171' : state.isFocused ? '#6366f1' : '#e5e7eb',
    backgroundColor: hasError ? '#fef2f2' : '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.3)' : 'none',
    '&:hover': { borderColor: hasError ? '#f87171' : '#6366f1' },
    minHeight: '42px',
    fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#eef2ff' : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#111827',
  }),
  menu: (base) => ({ ...base, borderRadius: '0.75rem', zIndex: 20 }),
});

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const totalAmount = useSelector(selectCartTotalAmount);

  const [form, setForm] = useState({
    name: '', phone: '', address: '',
    district_id: '', district: '',
    area_id: '', area: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingZones, setLoadingZones] = useState(false);
  const [pricing, setPricing] = useState(null);

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, name, discountAmount, isFreeShipping, explanationBn }
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Set right before navigating to the confirmation page after a successful order,
  // so clearing the cart doesn't trip the empty-cart redirect below and bounce the
  // customer back to the homepage instead of showing their confirmation.
  const orderPlacedRef = useRef(false);

  useEffect(() => {
    if (cartItems.length === 0 && !orderPlacedRef.current) {
      navigate('/');
    }
  }, [cartItems.length, navigate]);

  useEffect(() => {
    pushBeginCheckout(cartItems, totalAmount);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoadingCities(true);
    fetch(`${API_BASE}/api/public/shipping/cities/`)
      .then(r => r.json())
      .then(data => {
        setCities(data.cities || []);
        setPricing(data.pricing || null);
      })
      .catch(() => {})
      .finally(() => setLoadingCities(false));
  }, []);

  const courierCost = useMemo(() => {
    if (!pricing || !form.district_id) return null;
    return Number(form.district_id === 1 ? pricing.price_dhaka : pricing.price_outside_dhaka);
  }, [pricing, form.district_id]);

  // Shared with order submission below — prices are always looked up server-side from
  // these product/variant ids, never trusted from the client cart.
  const orderItemsPayload = useMemo(
    () => cartItems.map(i => ({
      product_id: i.id,
      variant_id: i.variantId || null,
      size: i.size || '',
      quantity: i.quantity,
    })),
    [cartItems]
  );

  const applyPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoApplying(true);
    setPromoError('');
    try {
      const res = await fetch(`${API_BASE}/api/public/marketing/promo-codes/validate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, items: orderItemsPayload, courier_cost: courierCost }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPromoError(data?.error || 'Failed to apply promo code.');
        setAppliedPromo(null);
        return;
      }
      setAppliedPromo({
        code: data.code,
        name: data.name,
        discountAmount: Number(data.discount_amount),
        isFreeShipping: !!data.is_free_shipping,
        explanationBn: data.explanation_bn || '',
      });
    } catch {
      setPromoError('Network error. Please try again.');
      setAppliedPromo(null);
    } finally {
      setPromoApplying(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  // For free-shipping promos, always mirror the live delivery cost (not the value captured
  // when the code was applied) so the waived amount stays exactly right if the district changes.
  const discountAmount = appliedPromo
    ? (appliedPromo.isFreeShipping ? (courierCost || 0) : appliedPromo.discountAmount)
    : 0;

  const cityOptions = useMemo(
    () => cities.map(c => ({ value: c.city_id, label: c.city_name })),
    [cities]
  );

  const zoneOptions = useMemo(
    () => zones.map(z => ({ value: z.zone_id, label: z.zone_name })),
    [zones]
  );

  const selectedCity = cityOptions.find(o => o.value === form.district_id) || null;
  const selectedZone = zoneOptions.find(o => o.value === form.area_id) || null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
    setApiError('');
  };

  const handleDistrictChange = (option) => {
    setForm(f => ({
      ...f,
      district_id: option ? option.value : '',
      district: option ? option.label : '',
      area_id: '',
      area: '',
    }));
    setZones([]);
    if (errors.district) setErrors(er => ({ ...er, district: '' }));
    setApiError('');

    if (option) {
      setLoadingZones(true);
      fetch(`${API_BASE}/api/public/shipping/zones/?city_id=${option.value}`)
        .then(r => r.json())
        .then(data => setZones(data.zones || []))
        .catch(() => {})
        .finally(() => setLoadingZones(false));

      pushAddShippingInfo(cartItems, totalAmount, option.value === 1);
    }
  };

  const handleAreaChange = (option) => {
    setForm(f => ({
      ...f,
      area_id: option ? option.value : '',
      area: option ? option.label : '',
    }));
    setApiError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) errs.phone = phoneErr;
    if (!form.address.trim()) errs.address = 'Delivery address is required.';
    else if (form.address.trim().length < 5) errs.address = 'Please enter a complete address.';
    if (!form.district_id) errs.district = 'Please select your district.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError('');
    try {
      const payload = {
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim(),
        customer_address: form.address.trim(),
        district: form.district,
        district_id: form.district_id,
        area: form.area || '',
        area_id: form.area_id || null,
        promo_code: appliedPromo?.code || '',
        items: orderItemsPayload,
      };

      const res = await fetch(`${API_BASE}/api/public/orders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data?.error || data?.detail || Object.values(data).flat().join(' ') || 'Failed to place order.';
        setApiError(msg);
        return;
      }

      orderPlacedRef.current = true;
      pushPageView(window.location.pathname, 'purchase');
      pushPurchase(cartItems, {
        transactionId: data.order_number,
        value: totalAmount,
        shipping: courierCost || 0,
        coupon: appliedPromo?.code || '',
        isDhaka: form.district_id === 1,
      });

      navigate('/order-confirmation', {
        state: {
          order: {
            orderNumber: data.order_number,
            name: form.name.trim(),
            phone: form.phone.trim(),
            address: form.address.trim(),
            district: form.district,
            area: form.area,
            items: cartItems.map(i => ({
              id: i.id, size: i.size, quantity: i.quantity, price: i.price, name: i.name, image: i.image,
            })),
            subtotal: totalAmount,
            discountAmount,
            promoCode: appliedPromo?.code || '',
            isFreeShipping: !!appliedPromo?.isFreeShipping,
            courierCost: courierCost || 0,
            total: Math.max(0, totalAmount + (courierCost || 0) - discountAmount),
          },
        },
      });
      dispatch(clearCart());
    } catch {
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Checkout form ───────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to shop
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Checkout</h1>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left: customer form */}
        <div className="flex-1">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Delivery Information</h2>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                    errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="01XXXXXXXXX (11 digits)"
                  maxLength={11}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                    errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address">
                  Delivery Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  value={form.address}
                  onChange={handleChange}
                  placeholder="House/flat, road, area, city"
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors resize-none ${
                    errors.address ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  }`}
                />
                {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
              </div>

              {/* District */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District <span className="text-red-500">*</span>
                </label>
                <Select
                  options={cityOptions}
                  value={selectedCity}
                  onChange={handleDistrictChange}
                  isLoading={loadingCities}
                  isClearable
                  placeholder="Select district…"
                  styles={selectStyles(!!errors.district)}
                  noOptionsMessage={() => 'No districts found'}
                />
                {errors.district && <p className="mt-1 text-xs text-red-500">{errors.district}</p>}
              </div>

              {/* Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Area <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <Select
                  options={zoneOptions}
                  value={selectedZone}
                  onChange={handleAreaChange}
                  isLoading={loadingZones}
                  isDisabled={!form.district_id || loadingZones}
                  isClearable
                  placeholder={form.district_id ? 'Select area…' : 'Select district first'}
                  styles={selectStyles(false)}
                  noOptionsMessage={() => 'No areas found'}
                />
              </div>

              {apiError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {apiError}
                </div>
              )}

              <div className="pt-2">
                <p className="text-xs text-gray-400 mb-3 text-center">
                  No payment required now. Our sales executive will contact you to confirm your order.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {submitting ? 'Placing Order…' : 'Place Order'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right: order summary */}
        <div className="lg:w-80">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Order Summary</h2>

            <div className="space-y-3 mb-4">
              {cartItems.map((item) => (
                <div key={`${item.id}-${item.size}`} className="flex items-start gap-3">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">Size: {item.size} · Qty: {item.quantity}</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">
                      ৳{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Promo code */}
            <div className="border-t border-gray-100 pt-3">
              {appliedPromo ? (
                <div>
                  <div className="flex items-center justify-between gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-1.5 text-sm text-green-800 min-w-0">
                      <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-semibold truncate">{appliedPromo.code}</span>
                      <span className="text-green-600 text-xs">{appliedPromo.isFreeShipping ? 'free shipping' : 'applied'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removePromoCode}
                      className="text-green-700 hover:text-green-900 flex-shrink-0"
                      aria-label="Remove promo code"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {appliedPromo.explanationBn && (
                    <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{appliedPromo.explanationBn}</p>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                      placeholder="Promo code"
                      className="flex-1 px-3 py-2 text-sm border rounded-xl uppercase focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={applyPromoCode}
                      disabled={promoApplying || !promoCode.trim()}
                      className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-xl hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {promoApplying ? 'Applying…' : 'Apply'}
                    </button>
                  </div>
                  {promoError && <p className="mt-1.5 text-xs text-red-500">{promoError}</p>}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-3 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>৳{totalAmount.toLocaleString()}</span>
              </div>
              {discountAmount > 0 && !appliedPromo?.isFreeShipping && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>- ৳{discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery charge</span>
                {courierCost !== null ? (
                  appliedPromo?.isFreeShipping ? (
                    <span className="font-medium">
                      <span className="line-through text-gray-400 mr-1.5">৳{courierCost.toLocaleString()}</span>
                      <span className="text-green-600 font-semibold">FREE</span>
                    </span>
                  ) : (
                    <span className="font-medium text-gray-700">৳{courierCost.toLocaleString()}</span>
                  )
                ) : (
                  <span className="text-gray-400 text-xs">Select district to calculate</span>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between font-semibold text-gray-900">
              <span>Total</span>
              {courierCost !== null ? (
                <span>৳{Math.max(0, totalAmount + courierCost - discountAmount).toLocaleString()}</span>
              ) : (
                <span className="text-gray-500 font-medium text-sm">
                  ৳{Math.max(0, totalAmount - discountAmount).toLocaleString()}{' '}
                  <span className="text-gray-400 font-normal">(+ delivery)</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
